import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import apiClient from "../apiClient";
import { formatMoney } from "../formatMoney";
import PageContext from "../PageContext.jsx";

const NetWorth = () => {
  const [rows, setRows] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [defaultCurrency, setDefaultCurrency] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [form, setForm] = useState({
    PeriodId: "",
    NetWorthAmount: "",
    Notes: "",
  });

  const sortedPeriods = useMemo(
    () => [...periods].sort((a, b) => String(b.DateEnd || "").localeCompare(String(a.DateEnd || "")) || String(b.Description || "").localeCompare(String(a.Description || ""))),
    [periods]
  );

  const loadData = async () => {
    const [periodsRes, netWorthRes, currenciesRes] = await Promise.all([
      apiClient.get("/periods"),
      apiClient.get("/net-worth"),
      apiClient.get("/currencies"),
    ]);
    const periodRows = Array.isArray(periodsRes.data) ? periodsRes.data : [];
    const netWorthRows = Array.isArray(netWorthRes.data) ? netWorthRes.data : [];
    const currencyRows = Array.isArray(currenciesRes.data) ? currenciesRes.data : [];
    const currentDefaultCurrency = currencyRows.find((row) => Number(row.IsDefault))?.Description || "";
    setPeriods(periodRows);
    setRows(netWorthRows);
    setDefaultCurrency(currentDefaultCurrency);
    setForm((prev) => ({
      ...prev,
      PeriodId: prev.PeriodId || String(periodRows[0]?.PeriodId || ""),
    }));
  };

  useEffect(() => {
    const run = async () => {
      try {
        await loadData();
      } catch (err) {
        setError(err?.response?.data?.message || "Failed to load net worth.");
      } finally {
        setLoading(false);
      }
    };
    run();
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setError("");
    setSuccess("");
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");
    if (!form.PeriodId || form.NetWorthAmount === "") {
      setError("Period and net worth amount are required.");
      return;
    }

    setSaving(true);
    try {
      await apiClient.post("/net-worth", {
        PeriodId: Number(form.PeriodId),
        NetWorthAmount: Number(form.NetWorthAmount),
        Notes: form.Notes,
      });
      await loadData();
      setSuccess("Net worth saved.");
    } catch (err) {
      setError(err?.response?.data?.message || "Could not save net worth.");
    } finally {
      setSaving(false);
    }
  };

  const handleRecalculate = async () => {
    setError("");
    setSuccess("");
    if (!form.PeriodId) {
      setError("Select a period first.");
      return;
    }

    setRecalculating(true);
    try {
      const res = await apiClient.post("/net-worth/recalculate", {
        PeriodId: Number(form.PeriodId),
        Notes: form.Notes,
      });
      await loadData();
      setForm((prev) => ({
        ...prev,
        NetWorthAmount: String(res.data?.netWorthAmount ?? ""),
      }));
      setSuccess("Net worth recalculated and saved.");
    } catch (err) {
      setError(err?.response?.data?.message || "Could not recalculate net worth.");
    } finally {
      setRecalculating(false);
    }
  };

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Net Worth</h1>
          <p className="page-subtitle"><Link to="/">Home</Link> / Net Worth</p>
        </div>
      </header>
      <PageContext
        items={[
          { label: "Home", to: "/" },
          { label: "Net Worth" },
        ]}
        note="Save or recalculate net worth for a selected reporting period."
      />

      {error && <p className="notice notice-error">{error}</p>}

      <section className="panel form-card">
        <form className="form" onSubmit={handleSave}>
          <p className="notice notice-muted">
            Shown in default currency: {defaultCurrency || "Default"}
          </p>

          <label htmlFor="net-worth-period">Period</label>
          <select id="net-worth-period" name="PeriodId" value={form.PeriodId} onChange={handleChange}>
            <option value="">Select period</option>
            {sortedPeriods.map((period) => (
              <option key={period.PeriodId} value={period.PeriodId}>
                {period.Description} {period.DateEnd ? `(${period.DateEnd})` : ""}
              </option>
            ))}
          </select>

          <label htmlFor="net-worth-amount">Net Worth Value</label>
          <input
            id="net-worth-amount"
            type="number"
            step="0.01"
            name="NetWorthAmount"
            value={form.NetWorthAmount}
            onChange={handleChange}
            placeholder="e.g. 1250000.00"
          />
          <div className="form-value-preview">
            Display: {formatMoney(form.NetWorthAmount || 0, defaultCurrency || "Default")}
          </div>

          <label htmlFor="net-worth-notes">Notes</label>
          <textarea
            id="net-worth-notes"
            name="Notes"
            value={form.Notes}
            onChange={handleChange}
            placeholder="Optional notes for this period's net worth"
            rows="3"
          />

          <div className="form-actions">
            <button className="btn" type="submit" disabled={saving || recalculating}>Save Net Worth</button>
            <button className="btn btn-secondary" type="button" disabled={saving || recalculating} onClick={handleRecalculate}>
              {recalculating ? "Calculating Net Worth..." : "Update From Balances And Assets"}
            </button>
          </div>

          {recalculating && <p className="notice notice-muted">Calculating net worth from the latest balances and asset values...</p>}
          <p className="notice notice-muted">
            The update button uses the latest available bank balance for each bank and all current asset values, converted into the default currency.
          </p>
          {success && <p className="notice notice-muted">{success}</p>}
        </form>
      </section>

      {loading ? (
        <p className="notice notice-muted">Loading net worth...</p>
      ) : (
        <section className="resource-list">
          {rows.length === 0 ? (
            <article className="panel empty-state">
              <h2>No net worth values recorded yet</h2>
              <p className="notice notice-muted">Select a reporting period above, then calculate or enter the first value.</p>
            </article>
          ) : (
            <div className="table-scroll">
              <table className="resource-table">
                <thead>
                  <tr>
                    <th>Period</th>
                    <th>Net Worth</th>
                    <th>Updated</th>
                    <th>Notes</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.PeriodId}>
                      <td data-label="Period"><strong>{row.PeriodDescription}</strong></td>
                      <td data-label="Net Worth">{row.NetWorthAmount == null ? "-" : formatMoney(row.NetWorthAmount, defaultCurrency || "Default")}</td>
                      <td data-label="Updated">{row.UpdatedAt || "-"}</td>
                      <td data-label="Notes">{row.Notes || "-"}</td>
                      <td data-label="Actions" className="resource-table-actions">
                        <button
                          className="btn btn-secondary"
                          onClick={() =>
                            setForm({
                              PeriodId: String(row.PeriodId),
                              NetWorthAmount: row.NetWorthAmount == null ? "" : String(row.NetWorthAmount),
                              Notes: row.Notes || "",
                            })
                          }
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
    </div>
  );
};

export default NetWorth;
